import type { FC } from 'react';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Inline, Stack, Text } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';

import { EDIT_PAGE_V2 } from '@confluence/named-routes';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { PageSelector, PageType } from '@confluence/page-selector';
import { useBooleanFeatureFlag } from '@confluence/session-data';
import { SelectedSpaceOnChangeProps, SpaceSelector } from '@confluence/space-selector';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils';
import { useIsFolderEnabled } from '@confluence/folder/entry-points/useIsFolderEnabled';
import { useClassificationChangeState } from '@confluence/data-classification';

import { useCreatePage } from './useCreatePage';

const i18n = defineMessages({
	modalTitle: {
		id: 'quick-summary.create-page-modal.title',
		defaultMessage: 'Select location',
		description: 'title for the create page modal',
	},
	modalBody: {
		id: 'quick-summary.create-page-modal.body',
		defaultMessage: 'Select the space and parent item for your generated page',
		description: 'title for the create page modal',
	},
	cancelButton: {
		id: 'quick-summary.create-page-modal.cancel-button',
		defaultMessage: 'Cancel',
		description: 'button to close the create page modal',
	},
	confirmButton: {
		id: 'quick-summary.create-page-modal.confirm-button',
		defaultMessage: 'Confirm',
		description: 'button to submit the create page modal and create a new page',
	},
});

export type CreatePageModalProps = {
	onClose: () => void;
	summaryText: string;
	spaceKey: string;
	contentId: string;
};

export const CreatePageModal: FC<CreatePageModalProps> = ({
	onClose,
	summaryText,
	spaceKey,
	contentId,
}) => {
	const { formatMessage } = useIntl();

	const [createPage, { loading }] = useCreatePage();

	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isWhiteboardInContentTreeEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');
	const isDatabaseCreationEnabled = useBooleanFeatureFlag('confluence.frontend.databases.enabled');
	const { isFolderEnabled } = useIsFolderEnabled();

	const [parentPageId, setParentPageId] = useState<string>(contentId);
	const [destinationSpaceKey, setDestinationSpaceKey] = useState<string>(spaceKey);

	const onPageSelectorChange = (selectedPage: PageType) => {
		setParentPageId(selectedPage.id);
	};

	const onSpaceSelectorChange = (selectedSpace: SelectedSpaceOnChangeProps) => {
		const { spaceKey: selectedSpaceKey, homepageId: selectedSpaceHomepageId } = selectedSpace;
		setDestinationSpaceKey(selectedSpaceKey);
		setParentPageId(selectedSpaceHomepageId);
	};

	const { push } = useRouteActions();

	const handleCreatePage = async () => {
		try {
			const result = await createPage({
				parentId: parentPageId,
				spaceKey: destinationSpaceKey,
				pageTitle: 'this is a placeholder title',
				content: summaryText,
			});

			const draftUrl = EDIT_PAGE_V2.toUrl({
				contentType: 'pages',
				spaceKey: destinationSpaceKey,
				contentId: result.data.newPage.page.id,
			});
			push(draftUrl);
		} catch (e) {
			// noop, will handle errors in follow up PR
		}
	};

	const { selectedClassification, classificationError } = useClassificationChangeState(
		contentId,
		destinationSpaceKey,
		spaceKey,
	);

	const requiredSpaceOperations = [
		{
			targetType: 'page',
			operation: 'create',
		},
	];

	return (
		<ModalTransition>
			<Modal onClose={onClose}>
				<ModalHeader>
					<ModalTitle>{formatMessage(i18n.modalTitle)}</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<Stack space="space.200">
						<Text>{formatMessage(i18n.modalBody)}</Text>
						<SpaceSelector
							onChange={onSpaceSelectorChange}
							operationsUsedToDetermineRestrictedStatus={requiredSpaceOperations}
							showRestrictedSpaces={false}
							filterDefaultFromResults={false}
							spaceKey={destinationSpaceKey}
							spaceClassification={selectedClassification}
							hasClassificationError={!!classificationError}
							isDisabled={loading}
							filterStarredFromRecent
							labelTextTransform="none"
							useMenuPortalTarget
							selectStyleOverrides={{
								menuPortal: (base) => ({
									...base,
									zIndex: 9999,
								}),
							}}
						/>
						<PageSelector
							key={`${destinationSpaceKey}${parentPageId}`}
							spaceKey={destinationSpaceKey}
							defaultPageId={parentPageId}
							isDisabled={loading}
							onChange={onPageSelectorChange}
							isWhiteboardInContentTreeEnabled={isWhiteboardInContentTreeEnabled}
							isDatabaseCreationEnabled={isDatabaseCreationEnabled}
							isFolderEnabled={isFolderEnabled}
							useMenuPortalTarget
							selectStyleOverrides={{
								menuPortal: (base) => ({
									...base,
									zIndex: 9999,
								}),
							}}
						/>
					</Stack>
				</ModalBody>
				<ModalFooter>
					<Inline space="space.100">
						<Button appearance="subtle" onClick={onClose}>
							{formatMessage(i18n.cancelButton)}
						</Button>
						<Button appearance="primary" isLoading={loading} onClick={handleCreatePage}>
							{formatMessage(i18n.confirmButton)}
						</Button>
					</Inline>
				</ModalFooter>
			</Modal>
		</ModalTransition>
	);
};
